import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, FileText, FileSignature, CheckCircle2, ClipboardList, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  document_review: FileText,
  document_request: FileText,
  application_status: ClipboardList,
  ica_reminder: FileSignature,
  default: Bell,
};
const TYPE_COLORS = {
  document_review: 'bg-blue-50 text-blue-600',
  document_request: 'bg-amber-50 text-amber-600',
  application_status: 'bg-emerald-50 text-emerald-600',
  ica_reminder: 'bg-violet-50 text-violet-600',
  default: 'bg-slate-50 text-slate-500',
};

export default function NotificationBell({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setData(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifs();
    // WebSocket connection for real-time notifications
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBase = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:/, wsProtocol);
    let ws = null;
    let pingInterval = null;

    const connectWs = () => {
      if (!wsBase) return;
      try {
        ws = new WebSocket(`${wsBase}/ws/notifications`);
        ws.onopen = () => {
          // Get session token from cookie
          const cookies = document.cookie.split(';').reduce((acc, c) => { const [k, v] = c.trim().split('='); acc[k] = v; return acc; }, {});
          const token = cookies.session_token;
          if (token) ws.send(JSON.stringify({ token }));
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
          }, 30000);
        };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'notification') {
              setData(prev => ({
                notifications: [msg.notification, ...prev.notifications],
                unread_count: prev.unread_count + 1,
              }));
            } else if (msg.type === 'connected') {
              setData(prev => ({ ...prev, unread_count: msg.unread_count }));
            }
          } catch {}
        };
        ws.onclose = () => { clearInterval(pingInterval); };
      } catch {}
    };
    connectWs();
    // Fallback polling every 30s (in case WS fails)
    const interval = setInterval(fetchNotifs, 30000);
    return () => { clearInterval(interval); clearInterval(pingInterval); if (ws) ws.close(); };
  }, [fetchNotifs]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      fetchNotifs();
    } catch (err) {
      // Notification mark-read is non-critical; avoid disrupting UX
    }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.post(`${API}/notifications/mark-read`, { notification_ids: [notif.id] }, { withCredentials: true });
        fetchNotifs();
      } catch (err) {
        // Non-critical: notification state will refresh on next poll
      }
    }
    if (notif.link_tab && onNavigate) {
      onNavigate(notif.link_tab);
      setOpen(false);
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell size={18} className="text-slate-500" />
        {data.unread_count > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notification-count">
            {data.unread_count > 9 ? '9+' : data.unread_count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Notifications</h3>
              <div className="flex items-center gap-2">
                {data.unread_count > 0 && (
                  <button onClick={markAllRead} className="text-[10px] text-[#002FA7] hover:underline font-medium" data-testid="mark-all-read-btn">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-0.5 text-slate-300 hover:text-slate-500">
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {data.notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell size={24} className="text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No notifications yet</p>
                </div>
              ) : (
                data.notifications.map(notif => {
                  const Icon = TYPE_ICONS[notif.type] || TYPE_ICONS.default;
                  const colorClass = TYPE_COLORS[notif.type] || TYPE_COLORS.default;
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleClick(notif)}
                      className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-50 hover:bg-slate-50/50 transition-colors
                        ${!notif.read ? 'bg-blue-50/30' : ''}`}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-slate-800 truncate">{notif.title}</span>
                          {!notif.read && <span className="w-1.5 h-1.5 bg-[#002FA7] rounded-full shrink-0" />}
                        </div>
                        <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2">{notif.message}</p>
                        <span className="text-[9px] text-slate-300 mt-0.5 block">{timeAgo(notif.created_at)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
